import { Input, Dialog, Portal, Stack } from '@chakra-ui/react'
import type { FC, FormEventHandler, ReactNode } from 'react'
import { useCallback } from 'react'
import { useState } from 'react'

import { Button } from '@app/components/ui/button'
import { Field } from '@app/components/ui/field'
import { toaster } from '@app/components/ui/toaster'
import { useBusinessContextCreateMutation } from '@graphql/queries'

interface Props {
  parentBusinessContextId: string | null
  children?: ReactNode
}

const CreateBusinessContextModal: FC<Props> = ({ parentBusinessContextId, children }) => {
  const [open, setOpen] = useState(false)
  const [, createContext] = useBusinessContextCreateMutation()
  const [loading, setLoading] = useState(false)

  const onSubmit: FormEventHandler<HTMLFormElement> = useCallback(
    async (e) => {
      e.preventDefault()
      e.stopPropagation()
      const formData = new FormData(e.currentTarget)

      const name = formData.get('name') as string

      setLoading(true)
      try {
        await createContext({
          input: {
            name,
            parentBusinessContextId
          }
        })

        toaster.create({
          title: 'Folder created successfully',
          type: 'success'
        })

        setOpen(!open)
      } finally {
        setLoading(false)
      }
    },
    [createContext, open, parentBusinessContextId]
  )

  return (
    <Dialog.Root placement="center" lazyMount onOpenChange={(e) => setOpen(e.open)} open={open}>
      <Dialog.Trigger asChild>{children}</Dialog.Trigger>
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header>
              <Dialog.Title>Add new folder</Dialog.Title>
            </Dialog.Header>
            <form onSubmit={onSubmit}>
              <Dialog.Body>
                <Stack>
                  <Field label="Folder name" required>
                    <Input autoFocus name="name" placeholder="Folder name" required autoComplete="off" />
                  </Field>
                </Stack>
              </Dialog.Body>
              <Dialog.Footer justifyContent="flex-end">
                <Dialog.ActionTrigger asChild>
                  <Button variant="outline">Cancel</Button>
                </Dialog.ActionTrigger>
                <Button loading={loading} type="submit">
                  Create
                </Button>
              </Dialog.Footer>
            </form>
            <Dialog.CloseTrigger />
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  )
}

export default CreateBusinessContextModal
